import {
  CoObjectItem,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkDropDownSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../../components/interfaces/CoObject";
import { CoObject, CoObjectTypeEnum, SourceEnum, TgkAmount, TgkTable } from "../../../api/models";
import { TgkDimensionAttribute } from "../../../api/models/tgk-dimension-attribute";
import { TgkText } from "../../../api/models/tgk-text";
import { inheritDimensions, inheritParameters } from "../../../utils/helpers/ComponentInstanceHelper";
import { defaultFormatting } from "../../../utils/constants/formats";
import { FormatTypeEnum } from "../../../api/models/format-type-enum";

/* TODO it seems the DB doesn't carry the info of the component objects
function mapCoObjectComponentInstanceToXmlObject(coObject: CoObject): CoObjectItem {
  return {
  ...mapCoObjectToCoObjectItem(coObject),
  info: {
      objectName: coObject.name,
      info: coObject.desc,
      objectType: coObject.type,
      componentReference: {
        componentLogicId: coObject.componentLogicIdReference,
        componentName: coObject.info.componentReference.componentName,
        componentDocumentName: coObject.info.componentReference.componentDocumentName,
      },
    },
  };
} */

function mapCoObjectToCoObjectItem(coObject: CoObject): CoObjectItem {
  return {
    isLocked: coObject.isLocked,
    id: coObject.id,
    documentId: coObject.documentId,
    contentControlId: coObject.contentControlId,
    info: {
      objectName: coObject.name,
      objectType: CoObjectTypeEnum[coObject.type],
      info: coObject.desc,
      isComponent: coObject.isComponent,
    },
    source: null,
  };
}

function mapCheckboxToXmlObject(coObject: CoObject): CoObjectItem {
  return mapCoObjectToCoObjectItem(coObject);
}

function mapFreeTextToXmlObject(coObject: CoObject): CoObjectItem {
  return mapCoObjectToCoObjectItem(coObject);
}

function mapTgkTextToToXmlObject(coObject: TgkText): CoObjectItem & { source: TgkTextSource } {
  return {
    ...mapCoObjectToCoObjectItem(coObject),
    source: {
      type: CoObjectTypeEnum.TGK_TEXT,
      dimensions: inheritDimensions(coObject.dimensions ?? []),
      dbApplication: { id: coObject.dbId, desc: coObject.dbId },
      analyticalWorkspace: coObject.analyticalWorkspace,
      dataset: coObject.dataset,
      richText: { id: coObject.richText.id, description: coObject.richText.description },
      ...(coObject.process && { process: coObject.process }),
    },
  };
}

function mapTgkTableToXmlObject(coObject: TgkTable): CoObjectItem & { source: TgkTableObjectSource } {
  const formPath = coObject.form.path.split(",");
  const baseCoObjectItem = mapCoObjectToCoObjectItem(coObject);
  return {
    ...baseCoObjectItem,
    info: {
      ...baseCoObjectItem.info,
      overrideData: coObject.overrideData ?? false,
      overrideStyle: coObject.overrideStyle ?? false,
    },
    source: {
      type: CoObjectTypeEnum.TABLE,
      objectSource: coObject.source,

      dbApplication: { id: coObject.form.dbId, desc: coObject.form.dbId },
      form: {
        code: coObject.form.formId,
        desc: coObject.form.formDesc,
        oid: formPath[formPath.length - 1],
        oidPath: formPath,
      },
      sheet: {
        uid: coObject.sheet,
        code: coObject.sheetCode,
        desc: coObject.sheet,
      },
      area: { name: coObject.area },
      chart: {
        name: coObject.chart,
      },
      path: formPath,
      elabResultType: coObject.elabResultType === "IMAGE_STRINGBASE64" ? "IMAGE_STRINGBASE64" : "STRINGBASE64", //if an old table with HTML elabResultType then override
      ...(coObject.elabResultType === "IMAGE_STRINGBASE64" && !coObject.inheritFormatImage
        ? {
            imageExtension: coObject.formatImage.imageExtension,
            ...(coObject.formatImage.imageResolution ? { imageResolution: coObject.formatImage.imageResolution } : {}),
          }
        : {}),
      inheritFormatImage: coObject.elabResultType === "IMAGE_STRINGBASE64" ? coObject.inheritFormatImage : null,
    },
    advanced: { ...defaultFormatting, inheritFormat: coObject.inheritFormat, choice: "SOURCE" },
    parameters: inheritParameters(coObject.parameters),
    inheritFormat: coObject.inheritFormat,
  };
}

function mapTgkAmountToXmlObject(coObject: TgkAmount): CoObjectItem & { source: TgkAmountObjectSource } {
  const choice = coObject.source === SourceEnum.FORM ? coObject.formatType ?? FormatTypeEnum.NUMBER : "SOURCE";

  return {
    ...mapCoObjectToCoObjectItem(coObject),
    source: {
      type: coObject.type as CoObjectTypeEnum.AMOUNT | CoObjectTypeEnum.AMOUNT_AIH,
      dimensions: inheritDimensions(coObject.dimensions ?? []),
      dbApplication: { id: coObject.dbId, desc: coObject.dbId },
      objectSource: coObject.source,
      form: {
        code: coObject.formId,
        desc: coObject.formDesc,
        oid: coObject.formId,
        oidPath: [coObject.formId],
      },
      sheet: {
        uid: coObject.sheet,
        code: coObject.sheetCode,
        desc: coObject.sheet,
      },
      area: { name: coObject.area },
      cell: coObject.cell,
    },
    ...(coObject.parameters && {
      parameters: inheritParameters(coObject.parameters),
    }),
    advanced: {
      ...defaultFormatting,
      inheritFormat: coObject.inheritFormat,
      choice: choice,
    },
    inheritFormat: coObject.inheritFormat,
    formatType: coObject.formatType ?? FormatTypeEnum.NUMBER,
  };
}

function mapTgkDimensionAttributeToXmlObject(
  coObject: TgkDimensionAttribute
): CoObjectItem & { source: TgkDimensionAttributeSource } {
  return {
    ...mapCoObjectToCoObjectItem(coObject),
    source: {
      type: CoObjectTypeEnum.DIMENSION_ATTRIBUTE,
      dimensions: inheritDimensions(coObject.dimensions ?? []),
      dbApplication: { id: coObject.dbId, desc: coObject.dbId },
      locale: "", //TODO coObject.locale,
    },
  };
}

function mapDropDownToXmlObject(coObject: CoObject): CoObjectItem & { source: TgkDropDownSource } {
  return {
    ...mapCoObjectToCoObjectItem(coObject),
    source: {
      dropDownList: coObject.values.map((val, index) => ({
        key: val.key,
        value: val.value,
        itemId: index,
        inEdit: false,
        keyError: undefined,
        valueError: undefined,
        isBlurredKey: false,
        isBlurredValue: false,
      })),
      type: CoObjectTypeEnum.DROPDOWN_LIST,
    },
  };
}

export const generateMissingXML = (serverObject: CoObject) => {
  if (serverObject.componentLogicIdReference) return null; //TODO component cannot be handled return mapCoObjectComponentInstanceToXmlObject(serverObject);

  switch (serverObject.type) {
    case CoObjectTypeEnum.AMOUNT:
      return mapTgkAmountToXmlObject(serverObject as TgkAmount);
    case CoObjectTypeEnum.DIMENSION_ATTRIBUTE:
      return mapTgkDimensionAttributeToXmlObject(serverObject as TgkDimensionAttribute);
    case CoObjectTypeEnum.TABLE:
    case CoObjectTypeEnum.CHART:
      return mapTgkTableToXmlObject(serverObject as TgkTable);
    case CoObjectTypeEnum.TGK_TEXT:
      return mapTgkTextToToXmlObject(serverObject as TgkText);
    case CoObjectTypeEnum.CHECKBOX:
      return mapCheckboxToXmlObject(serverObject);
    case CoObjectTypeEnum.DROPDOWN_LIST:
      return mapDropDownToXmlObject(serverObject);
    case CoObjectTypeEnum.FREE_TEXT:
      return mapFreeTextToXmlObject(serverObject);
    default:
      return null;
  }
};
